/* eslint-disable no-restricted-syntax */
import { useCallback, useEffect, useMemo, useState } from "react";

import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import {
  useForm,
  FormProvider,
  Controller,
  useWatch,
  useFormContext,
} from "react-hook-form";

import map from "lodash/map";
import clsx from "clsx";
import Select from "react-select";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { isValidPhoneNumber } from "libphonenumber-js/min";

import {
  QUERY_DIGITAL_RECEPTION_MANUAL_REGISTRATIONS,
  QUERY_DIGITAL_RECEPTION_SETTINGS,
  QUERY_SHELLY_CONTROLLERS,
  QUERY_TUYA_CONTROLLERS,
} from "../../../../../config/graphql/query";
import { UPDATE_DIGITAL_RECEPTION_SETTINGS } from "../../../../../config/graphql/mutation";

import Input from "../../../../../components/Input";
import BrotherPrinterSetting from "./BortherPrinterSetting";

import { useCurrentHasPlan } from "../../../../../components/PlanBlock";
import useHasRole from "../../../../../lib/hooks/useHasRole";
import {
  CUSTOMIZABLE_ANIMATIONS_RECEPTIONS,
  GENERAL_SETTINGS_SECTIONS_BY_RECEPTION,
  GeneralSettingsSection,
  POST_CHECKIN_FORM_RECEPTIONS,
} from "../../../../../config/const/common";
import { LayoutOptionRegistration } from "../../../../../components/DigitalReception/Layout";
import {
  useIsNfcEnabled,
  useIsPhotoCaptureEnabled,
} from "../../../../Clients/Client/hooks/useClient";

import AccessControlSettings from "./ShellySettings";

interface FormValues extends IDigitalReceptionSettings {}

const overviewFieldsOptions: OverviewField[] = [
  "NAME",
  "TYPE",
  "CHECKIN_TIME",
  "HOST",
  "CHECKOUT",
];

const OverviewWithoutCodeSetting = ({ disabled }: { disabled: boolean }) => {
  const { t } = useTranslation(["screens", "common"]);

  const [confirming, setConfirming] = useState(false);

  const fields: OverviewField[] = useWatch({ name: "overview.fields" }) || [];

  const withCheckout = fields.includes("CHECKOUT");

  return (
    <Controller
      name="overview.openWithoutCode"
      render={({ field: { value, name, onChange, ...rest } }) => (
        <>
          <div className="form-check mt-2">
            <input
              id={name}
              {...rest}
              type="checkbox"
              className="form-check-input"
              checked={value === true}
              onChange={() => (value ? onChange(false) : setConfirming(true))}
              disabled={disabled}
            />
            <label className="form-check-label" htmlFor={name}>
              {t("screens:screen.settings.overview.openWithoutCode.label")}
            </label>
          </div>

          <Modal show={confirming} onHide={() => setConfirming(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>
                {t(
                  "screens:screen.settings.overview.openWithoutCode.modal.title",
                )}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>
                <strong>
                  {t(
                    "screens:screen.settings.overview.openWithoutCode.warning.title",
                  )}
                </strong>
              </p>
              <p>
                {t(
                  "screens:screen.settings.overview.openWithoutCode.warning.body",
                )}
              </p>
              {withCheckout && (
                <p>
                  {t(
                    "screens:screen.settings.overview.openWithoutCode.warning.checkout",
                  )}
                </p>
              )}
              <p className="mb-0">
                {t(
                  "screens:screen.settings.overview.openWithoutCode.modal.question",
                )}
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button size="sm" onClick={() => setConfirming(false)}>
                {t("common:cancel")}
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() => {
                  onChange(true);
                  setConfirming(false);
                }}
              >
                {t(
                  "screens:screen.settings.overview.openWithoutCode.modal.confirm",
                )}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    />
  );
};

const DEFAULT_RING_DURATION = 30;

const RING_DURATION_RANGE = { min: 16, max: 600 };
const MAX_DURATION_RANGE = { min: 30, max: 14400 };

const isDurationInRange = (
  value: unknown,
  { min, max }: { min: number; max: number },
  allowEmpty: boolean,
) => {
  if (value === "" || value === null || value === undefined) {
    return allowEmpty;
  }

  const parsed = Number(value);

  return Number.isInteger(parsed) && parsed >= min && parsed <= max;
};

const MemberPhotoCaptureSettings = ({
  type,
  disabled,
}: {
  type: "employee" | "visitor" | "meetingAttendee";
  disabled: boolean;
}) => {
  const { t } = useTranslation(["screens"]);

  const { setValue } = useFormContext();

  const photoCaptureMemberEnabled = useWatch({
    name: `photoCapture.${type}.enabled`,
  });

  useEffect(() => {
    if (!photoCaptureMemberEnabled) {
      setValue(`photoCapture.${type}.required`, false);
    }
  }, [photoCaptureMemberEnabled, setValue, type]);

  return (
    <div className="mb-2">
      <h6>{t(`screens:screen.settings.photoCapture.${type}.title`)}</h6>
      <Controller
        name={`photoCapture.${type}.enabled`}
        render={({ field: { value = false, name, onChange, ...rest } }) => (
          <div className="form-check ml-3">
            <input
              id={name}
              {...rest}
              type="checkbox"
              className="form-check-input"
              checked={value === true}
              onChange={() => onChange(!value)}
              disabled={disabled}
            />
            <label className="form-check-label" htmlFor={name}>
              {t(`screens:screen.settings.photoCapture.${type}.enabled`)}
            </label>
          </div>
        )}
      />
      <Controller
        name={`photoCapture.${type}.required`}
        render={({ field: { value = false, name, onChange, ...rest } }) => (
          <div className="form-check ml-3">
            <input
              id={name}
              {...rest}
              type="checkbox"
              className="form-check-input"
              checked={value === true}
              onChange={() => onChange(!value)}
              disabled={!photoCaptureMemberEnabled || disabled}
            />
            <label className="form-check-label" htmlFor={name}>
              {t(`screens:screen.settings.photoCapture.${type}.required`)}
            </label>
          </div>
        )}
      />
    </div>
  );
};

const GeneralSettings = () => {
  const methods = useForm<FormValues>({
    shouldUnregister: false,
  });

  const { screenId } = useParams<{ screenId: string }>();

  const { t } = useTranslation(["screens", "common"]);

  const isFree = useCurrentHasPlan(["free"]);

  const isAdmin = useHasRole(["ADMIN"]);
  const isClientAdmin = useHasRole(["CLIENT_ADMIN"]);

  const disabled = isFree || !(isAdmin || isClientAdmin);

  const isClientPhotoCaptureEnabled = useIsPhotoCaptureEnabled();
  const isClientNfcEnabled = useIsNfcEnabled();

  const photoCaptureEnabled = methods.watch("photoCapture.enabled");

  const scannerEnabled = methods.watch("scanner.enabled");
  const scannerAutoScanEnabled = methods.watch("scanner.autoScan.enabled");
  const nfcEnabled = methods.watch("nfc.enabled");

  const { data } = useQuery<{
    digitalReception: IDigitalReception;
  }>(QUERY_DIGITAL_RECEPTION_SETTINGS, {
    skip: !screenId,
    variables: {
      id: screenId,
    },
    fetchPolicy: "network-only",
    onCompleted: ({ digitalReception: { settings } }) => {
      methods.reset({
        ...(settings ?? {}),
        call: {
          ringDuration: settings?.call?.ringDuration ?? DEFAULT_RING_DURATION,
          maxDuration: settings?.call?.maxDuration ?? null,
        },
      });
    },
  });

  const template = data?.digitalReception?.template;
  const communicationProvider = data?.digitalReception?.communicationProvider;

  const isAnimationsCustomizable = useMemo(
    () => CUSTOMIZABLE_ANIMATIONS_RECEPTIONS.includes(template || ""),
    [template],
  );

  const isPostCheckinFormAvailable = useMemo(
    () => POST_CHECKIN_FORM_RECEPTIONS.includes(template || ""),
    [template],
  );

  const templateSections =
    GENERAL_SETTINGS_SECTIONS_BY_RECEPTION[template || ""];

  const isVisible = useCallback(
    (section: GeneralSettingsSection) =>
      // Not until the template is known, or every section flashes up and then disappears.
      !!template && (!templateSections || templateSections.includes(section)),
    [template, templateSections],
  );

  // A column with nothing to show is hidden, so the next one starts from the left edge.
  const hasVisible = (sections: GeneralSettingsSection[]) =>
    sections.some(isVisible);

  const showReceptionistPhone =
    isVisible("receptionistPhone") &&
    (!communicationProvider ||
      communicationProvider === "twilio" ||
      !!templateSections?.includes("receptionistPhone"));

  const showReceptionistTeams =
    isVisible("receptionistTeams") && communicationProvider === "teams";

  // Only sets made for this: a check-in set would ask for the name a second time. Read past the
  // cache -- a set created in the next tab would otherwise be missing until a full reload.
  const { data: formRegistrationsData, loading: formRegistrationsLoading } =
    useQuery<{
      manualRegistrations: IManualRegistration[];
    }>(QUERY_DIGITAL_RECEPTION_MANUAL_REGISTRATIONS, {
      variables: { filter: { screen: screenId, purpose: "form" } },
      skip: !screenId || !isPostCheckinFormAvailable,
      fetchPolicy: "network-only",
    });

  const { data: shellyControllersData } = useQuery<{
    shellyControllers: IShellyController[];
  }>(QUERY_SHELLY_CONTROLLERS);

  const [onUpdate] = useMutation(UPDATE_DIGITAL_RECEPTION_SETTINGS, {
    refetchQueries: [
      {
        query: QUERY_DIGITAL_RECEPTION_SETTINGS,
        variables: { id: screenId },
      },
    ],
  });

  const { data: tuyaControllersData } = useQuery<{
    tuyaControllers: ITuyaController[];
  }>(QUERY_TUYA_CONTROLLERS);

  const overviewFieldsSelectOptions = map(overviewFieldsOptions, (v) => ({
    value: v,
    label: t(`screens:screen.settings.overview.fieldOptions.${v}`),
  }));

  const shellyEnabled = methods.watch("shelly.enabled");
  const tuyaEnabled = methods.watch("tuya.enabled");

  const openWithoutCode = methods.watch("overview.openWithoutCode");

  const onSubmit = useCallback(
    async (settings: FormValues) => {
      const {
        support,
        receptionistPhone,
        receptionistTeams,
        call,
        dispenser,
        deliveryReceiver,
        overview,
        animations,
        printing,
        wifi,
        photoCapture,
        contactPreview,
        teamsVideoCall,
        shelly,
        scanner,
        nfc,
        bell,
        tuya,
        layout,
      } = settings;

      const { securityType, ssid, password } = wifi ?? {};

      if (
        (securityType || ssid || password) &&
        !(securityType && ssid && password)
      ) {
        toast.error<string>(
          t("screens:screen.screenRoute.toast.incompleteWifiData"),
        );
        return;
      }

      onUpdate({
        variables: {
          input: {
            id: screenId,
            settings: {
              support: {
                email: support?.email || null,
                phone: support?.phone || null,
              },
              receptionistPhone: {
                primary: receptionistPhone?.primary || null,
                backup: receptionistPhone?.backup || null,
              },
              receptionistTeams: {
                email: receptionistTeams?.email || null,
                groupId: receptionistTeams?.groupId || null,
              },
              call: {
                ringDuration: call?.ringDuration
                  ? parseInt(String(call.ringDuration), 10)
                  : 30,
                maxDuration: call?.maxDuration
                  ? parseInt(String(call.maxDuration), 10)
                  : null,
              },
              dispenser: {
                id: dispenser?.id || null,
                socketUrl: dispenser?.socketUrl || null,
              },
              deliveryReceiver: {
                fullName: deliveryReceiver?.fullName || null,
                email: deliveryReceiver?.email || null,
                phone: deliveryReceiver?.phone || null,
              },
              overview: {
                code: overview?.code || null,
                // ⚠️ This payload is hand-built field by field, so anything missing here
                // never reaches the server and silently fails to save.
                openWithoutCode: overview?.openWithoutCode === true,
                fields: overview?.fields || [],
              },
              animations: {
                enabled: animations?.enabled,
              },
              printing: {
                enabled: printing?.enabled,
                isAirPrinting: printing?.isAirPrinting,
                isHorizontal: printing?.isHorizontal,
                printer: printing?.printer || null,
                label: {
                  width: printing?.label?.width || null,
                  height: printing?.label?.height || null,
                },
              },
              wifi:
                securityType && ssid && password
                  ? {
                      securityType,
                      ssid,
                      hiddenSsid: wifi?.hiddenSsid || false,
                      password,
                    }
                  : null,
              photoCapture: {
                enabled: photoCapture?.enabled || false,
                employee: {
                  enabled: photoCapture?.employee?.enabled || false,
                  required: photoCapture?.employee?.required || false,
                },
                visitor: {
                  enabled: photoCapture?.visitor?.enabled || false,
                  required: photoCapture?.visitor?.required || false,
                },
                meetingAttendee: {
                  enabled: photoCapture?.meetingAttendee?.enabled || false,
                  required: photoCapture?.meetingAttendee?.required || false,
                },
              },
              contactPreview: {
                enabled: contactPreview?.enabled,
              },
              teamsVideoCall: {
                enabled: teamsVideoCall?.enabled,
              },
              shelly: {
                enabled: shelly?.enabled || false,
                controller: shelly?.controller || null,
                checkin: {
                  enabled: shelly?.checkin?.enabled || false,
                  autoSwitch: shelly?.checkin?.autoSwitch || false,
                  employee: {
                    enabled: shelly?.checkin?.employee?.enabled || false,
                  },
                  visitor: {
                    enabled: shelly?.checkin?.visitor?.enabled || false,
                  },
                  guest: {
                    enabled: shelly?.checkin?.guest?.enabled || false,
                  },
                  meetingAttendee: {
                    enabled: shelly?.checkin?.meetingAttendee?.enabled || false,
                  },
                },
                checkout: {
                  enabled: shelly?.checkout?.enabled || false,
                  autoSwitch: shelly?.checkout?.autoSwitch || false,
                  employee: {
                    enabled: shelly?.checkout?.employee?.enabled || false,
                  },
                  visitor: {
                    enabled: shelly?.checkout?.visitor?.enabled || false,
                  },
                  guest: {
                    enabled: shelly?.checkout?.guest?.enabled || false,
                  },
                  meetingAttendee: {
                    enabled:
                      shelly?.checkout?.meetingAttendee?.enabled || false,
                  },
                },
              },
              scanner: {
                enabled: scanner?.enabled || false,
                autoScan: {
                  enabled: scanner?.autoScan?.enabled || false,
                  type: scanner?.autoScan?.type || "CHECKIN_AND_CHECKOUT",
                  onlyQrScanner: scanner?.autoScan?.onlyQrScanner || false,
                },
              },
              nfc: {
                enabled: nfc?.enabled || false,
                socketUrl: nfc?.socketUrl || null,
              },
              bell: {
                enabled: bell?.enabled,
              },
              ...(isPostCheckinFormAvailable && {
                layout: {
                  postCheckinForm: {
                    enabled: layout?.postCheckinForm?.enabled || false,
                    // null, not undefined: a deep merge drops undefined and would keep the old set.
                    registration:
                      layout?.postCheckinForm?.registration?.id || null,
                  },
                },
              }),
              tuya: {
                enabled: tuya?.enabled || false,
                controller: tuya?.controller || null,
                checkin: {
                  enabled: tuya?.checkin?.enabled || false,
                  autoSwitch: tuya?.checkin?.autoSwitch || false,
                  employee: {
                    enabled: tuya?.checkin?.employee?.enabled || false,
                  },
                  visitor: {
                    enabled: tuya?.checkin?.visitor?.enabled || false,
                  },
                  guest: {
                    enabled: tuya?.checkin?.guest?.enabled || false,
                  },
                  meetingAttendee: {
                    enabled: tuya?.checkin?.meetingAttendee?.enabled || false,
                  },
                },
                checkout: {
                  enabled: tuya?.checkout?.enabled || false,
                  autoSwitch: tuya?.checkout?.autoSwitch || false,
                  employee: {
                    enabled: tuya?.checkout?.employee?.enabled || false,
                  },
                  visitor: {
                    enabled: tuya?.checkout?.visitor?.enabled || false,
                  },
                  guest: {
                    enabled: tuya?.checkout?.guest?.enabled || false,
                  },
                  meetingAttendee: {
                    enabled: tuya?.checkout?.meetingAttendee?.enabled || false,
                  },
                },
              },
            },
          },
        },
      }).then(() => {
        toast.success<string>(t("screens:screen.screenRoute.toast.updated"));
      });
    },
    [onUpdate, t, screenId, isPostCheckinFormAvailable],
  );

  // Nothing to show, so no form either: a lone Submit would save settings nobody can see.
  if (templateSections?.length === 0) {
    return (
      <div className="container-fluid">
        <p className="text-muted">
          {t("screens:screen.settings.noGeneralSettings")}
        </p>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <FormProvider {...methods}>
        <form
          className="row"
          noValidate
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <div className="col-lg-3">
            {isVisible("teamsVideoCall") && (
              <div className="form-group mb-4">
                <h6>{t("screens:screen.settings.teamsVideoCall.title")}</h6>
                <Controller
                  name="teamsVideoCall.enabled"
                  render={({ field: { value, name, onChange, ...rest } }) => {
                    return (
                      <div className="form-check my-2">
                        <input
                          id={name}
                          {...rest}
                          type="checkbox"
                          className="form-check-input"
                          checked={value === true}
                          onChange={() => onChange(!value)}
                          disabled={!isAdmin}
                        />
                        <label className="form-check-label" htmlFor={name}>
                          {t("screens:screen.settings.teamsVideoCall.enabled")}
                        </label>
                      </div>
                    );
                  }}
                />
                <p className="small">
                  {t("screens:screen.settings.teamsVideoCall.helperText")}
                </p>
              </div>
            )}

            {(showReceptionistPhone || showReceptionistTeams) && (
              <div className="form-group mb-4">
                <h6>{t("screens:screen.settings.receptionist.title")}</h6>
                {showReceptionistPhone && (
                  <div className="form-group">
                    <label htmlFor="phone">
                      {t("screens:screen.settings.receptionist.primaryPhone")}
                    </label>
                    <Input
                      name="receptionistPhone.primary"
                      className="form-control"
                      rules={{
                        validate: {
                          invalid: (value) => {
                            if (!value) {
                              return true;
                            }
                            return (
                              isValidPhoneNumber(value) ||
                              t("employee:input.error.phone.format")
                            );
                          },
                        },
                      }}
                      disabled={disabled}
                    />
                    <small className="form-text text-muted small">
                      {t("employee:input.label.phone-help")}
                    </small>
                  </div>
                )}
                {showReceptionistTeams && (
                  <div className="form-group">
                    <label htmlFor="emailTeams">
                      {t("screens:screen.settings.receptionist.emailTeams")}
                    </label>
                    <Input
                      name="receptionistTeams.email"
                      autoComplete="email"
                      className="form-control"
                      disabled={disabled}
                    />
                  </div>
                )}
              </div>
            )}
            {isVisible("deliveryReceiver") && (
              <div className="form-group mb-4">
                <h6>{t("screens:screen.settings.deliveryReceiver.title")}</h6>
                <div className="form-group mb-2">
                  <label htmlFor="deliveryFullName">
                    {t("screens:screen.settings.deliveryReceiver.fullName")}
                  </label>
                  <Input
                    name="deliveryReceiver.fullName"
                    className="form-control"
                    disabled={disabled}
                  />
                </div>
                <div className="form-group mb-2">
                  <label htmlFor="deliveryEmail">
                    {t(
                      `screens:screen.settings.deliveryReceiver.email${
                        communicationProvider === "teams" ? "Teams" : ""
                      }`,
                    )}
                  </label>
                  <Input
                    name="deliveryReceiver.email"
                    autoComplete="email"
                    className="form-control"
                    disabled={disabled}
                  />
                </div>
                {(!communicationProvider ||
                  communicationProvider === "twilio") && (
                  <div className="form-group">
                    <label htmlFor="deliveryPhone">
                      {t("screens:screen.settings.deliveryReceiver.phone")}
                    </label>
                    <Input
                      name="deliveryReceiver.phone"
                      className="form-control"
                      rules={{
                        validate: {
                          invalid: (value) => {
                            if (!value) {
                              return true;
                            }
                            return (
                              isValidPhoneNumber(value) ||
                              t("employee:input.error.phone.format")
                            );
                          },
                        },
                      }}
                      disabled={disabled}
                    />
                    <small className="form-text text-muted small">
                      {t("employee:input.label.phone-help")}
                    </small>
                  </div>
                )}
              </div>
            )}
            <input
              type="submit"
              className="btn btn-primary"
              disabled={
                methods.formState.isSubmitting || !(isAdmin || isClientAdmin)
              }
            />
          </div>
          <div
            className={clsx(
              "col-lg-3",
              !hasVisible([
                "bell",
                "animations",
                "overview",
                "printing",
                "contactPreview",
                "postCheckinForm",
                "call",
              ]) && "d-none",
            )}
          >
            {isVisible("bell") && (
              <div className="form-group mb-4">
                <h6>{t("screens:screen.settings.bell.title")}</h6>
                <Controller
                  name="bell.enabled"
                  render={({ field: { value, name, onChange, ...rest } }) => {
                    return (
                      <div className="form-check my-2">
                        <input
                          id={name}
                          {...rest}
                          type="checkbox"
                          className="form-check-input"
                          checked={value === true}
                          onChange={() => onChange(!value)}
                          disabled={!(isAdmin || isClientAdmin)}
                        />
                        <label className="form-check-label" htmlFor={name}>
                          {t("screens:screen.settings.bell.enabled")}
                        </label>
                      </div>
                    );
                  }}
                />
                {/* <p className="small">
                  {t("screens:screen.settings.bell.helperText")}
                </p> */}
              </div>
            )}
            {isVisible("animations") && isAnimationsCustomizable && (
              <div className="form-group mb-4">
                <h6>{t("screens:screen.settings.animations.title")}</h6>
                <Controller
                  name="animations.enabled"
                  render={({ field: { value, name, onChange, ...rest } }) => {
                    return (
                      <div className="form-check my-2">
                        <input
                          id={name}
                          {...rest}
                          type="checkbox"
                          className="form-check-input"
                          checked={value === true}
                          onChange={() => onChange(!value)}
                          disabled={disabled}
                        />
                        <label className="form-check-label" htmlFor={name}>
                          {t("screens:screen.settings.animations.enabled")}
                        </label>
                      </div>
                    );
                  }}
                />
              </div>
            )}
            {isVisible("overview") && (
              <div className="form-group mb-4">
                <h6>{t("screens:screen.settings.overview.title")}</h6>
                <OverviewWithoutCodeSetting disabled={disabled} />
                <div className="form-group mt-3">
                  <label htmlFor="overviewCode">
                    {t("screens:screen.settings.overview.code")}
                  </label>
                  <Input
                    name="overview.code"
                    type="password"
                    className="form-control"
                    maxLength={6}
                    rules={{
                      minLength: {
                        value: 6,
                        message: t(
                          "screens:screen.settings.overview.error.minLength",
                          {
                            count: 6,
                          },
                        ),
                      },
                      maxLength: {
                        value: 6,
                        message: t(
                          "screens:screen.settings.overview.error.maxLength",
                          {
                            count: 6,
                          },
                        ),
                      },
                      validate: {
                        format: (value) => {
                          if (!value) {
                            return true;
                          }
                          return (
                            /^[0-9]+$/.test(value) ||
                            t("screens:screen.settings.overview.error.format")
                          );
                        },
                      },
                    }}
                    disabled={disabled || openWithoutCode}
                  />
                  {openWithoutCode && (
                    <p className="mt-1 small text-muted">
                      {t(
                        "screens:screen.settings.overview.openWithoutCode.codeUnused",
                      )}
                    </p>
                  )}
                </div>
                <Controller
                  name="overview.fields"
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <div className="form-group">
                      <label htmlFor="overviewFields">
                        {t("screens:screen.settings.overview.fields")}
                      </label>
                      <Select
                        closeMenuOnSelect={false}
                        isMulti
                        options={overviewFieldsSelectOptions}
                        onChange={(nextValue) => {
                          const nextFields: OverviewField[] = map(
                            nextValue,
                            "value",
                          );

                          onChange(nextFields);
                        }}
                        value={map(value, (value) => ({
                          value,
                          label: t(
                            `screens:screen.settings.overview.fieldOptions.${value}`,
                          ),
                        }))}
                        className={clsx({
                          "is-invalid": !!error,
                        })}
                        isDisabled={disabled}
                      />
                      {!!error && (
                        <div className="invalid-feedback">{error.message}</div>
                      )}
                    </div>
                  )}
                />
              </div>
            )}
            {isVisible("printing") && (
              <BrotherPrinterSetting disabled={disabled} screenId={screenId} />
            )}
            {isVisible("contactPreview") && (
              <div className="form-group mb-4">
                <h6>{t("screens:screen.settings.contactPreview.title")}</h6>
                <Controller
                  name="contactPreview.enabled"
                  render={({ field: { value, name, onChange, ...rest } }) => {
                    return (
                      <div className="form-check mt-2">
                        <input
                          id={name}
                          {...rest}
                          type="checkbox"
                          className="form-check-input"
                          checked={value === true}
                          onChange={() => onChange(!value)}
                          disabled={disabled}
                        />
                        <label className="form-check-label" htmlFor={name}>
                          {t("screens:screen.settings.contactPreview.enabled")}
                        </label>
                      </div>
                    );
                  }}
                />
                <p className="mt-1 small">
                  {t("screens:screen.settings.contactPreview.helperText")}
                </p>
              </div>
            )}
            {isVisible("postCheckinForm") && isPostCheckinFormAvailable && (
              <div className="form-group mb-4">
                <h6>{t("screens:screen.settings.postCheckinForm.title")}</h6>
                <Controller
                  name="layout.postCheckinForm.enabled"
                  render={({ field: { value, name, onChange, ...rest } }) => (
                    <div className="form-check mt-2">
                      <input
                        id={name}
                        {...rest}
                        type="checkbox"
                        className="form-check-input"
                        checked={value === true}
                        onChange={() => onChange(!value)}
                        disabled={disabled}
                      />
                      <label className="form-check-label" htmlFor={name}>
                        {t("screens:screen.settings.postCheckinForm.enabled")}
                      </label>
                    </div>
                  )}
                />
                <div className="mt-2">
                  <LayoutOptionRegistration
                    name="layout.postCheckinForm"
                    manualRegistrations={
                      formRegistrationsData?.manualRegistrations ?? []
                    }
                    loading={formRegistrationsLoading}
                    label={t(
                      "screens:screen.settings.postCheckinForm.registration",
                    )}
                  />
                </div>
                <p className="mt-1 small">
                  {t("screens:screen.settings.postCheckinForm.helperText")}
                </p>
              </div>
            )}
            {isVisible("call") &&
              (!communicationProvider ||
                communicationProvider === "twilio") && (
                <div className="form-group mb-4">
                  <h6>{t("screens:screen.settings.call.title")}</h6>
                  <p className="mt-1 small">
                    {t("screens:screen.settings.call.helperText")}
                  </p>
                  <div className="form-group">
                    <label htmlFor="callRingDuration">
                      {t("screens:screen.settings.call.ringDuration")}
                    </label>
                    <Input
                      name="call.ringDuration"
                      type="number"
                      min={RING_DURATION_RANGE.min}
                      max={RING_DURATION_RANGE.max}
                      step={1}
                      className="form-control"
                      disabled={disabled}
                      rules={{
                        validate: (value) =>
                          isDurationInRange(
                            value,
                            RING_DURATION_RANGE,
                            false,
                          ) ||
                          t(
                            "screens:screen.settings.call.durationError",
                            RING_DURATION_RANGE,
                          ),
                      }}
                    />
                    <small className="form-text text-muted small">
                      {t("screens:screen.settings.call.ringDurationHelp")}
                    </small>
                  </div>
                  <div className="form-group">
                    <label htmlFor="callMaxDuration">
                      {t("screens:screen.settings.call.maxDuration")}
                    </label>
                    <Input
                      name="call.maxDuration"
                      type="number"
                      min={MAX_DURATION_RANGE.min}
                      max={MAX_DURATION_RANGE.max}
                      step={1}
                      className="form-control"
                      disabled={disabled}
                      rules={{
                        // Empty is a real value here: it means the call is never cut short.
                        validate: (value) =>
                          isDurationInRange(value, MAX_DURATION_RANGE, true) ||
                          t(
                            "screens:screen.settings.call.maxDurationError",
                            MAX_DURATION_RANGE,
                          ),
                      }}
                    />
                    <small className="form-text text-muted small">
                      {t("screens:screen.settings.call.maxDurationHelp")}
                    </small>
                  </div>
                </div>
              )}
          </div>
          <div
            className={clsx(
              "col-lg-3",
              !hasVisible(["wifi", "accessControl"]) && "d-none",
            )}
          >
            {isVisible("wifi") && (
              <div className="form-group mb-4">
                <h6>{t("screens:screen.settings.wifi.title")}</h6>
                <div className="form-group mb-2">
                  <label htmlFor="wifi.securityType">
                    {t("screens:screen.settings.wifi.securityType")}
                  </label>
                  <select
                    {...methods.register("wifi.securityType")}
                    id="wifi.securityType"
                    className="custom-select"
                    disabled={disabled}
                  >
                    <option value="" />
                    <option value="WPA">WPA</option>
                    <option value="WPA2">WPA2</option>
                    <option value="WPA3">WPA3</option>
                    <option value="WEP">WEP</option>
                  </select>
                </div>
                <div className="form-group mb-2">
                  <label htmlFor="wifi.ssid">
                    {t("screens:screen.settings.wifi.ssid")}
                  </label>
                  <Input
                    id="wifi.ssid"
                    name="wifi.ssid"
                    className="form-control"
                    disabled={disabled}
                  />
                </div>
                <div className="form-group mb-2">
                  <Controller
                    name="wifi.hiddenSsid"
                    render={({ field: { value, name, onChange, ...rest } }) => {
                      return (
                        <div className="form-check my-1">
                          <input
                            id={name}
                            {...rest}
                            type="checkbox"
                            className="form-check-input"
                            checked={value === true}
                            onChange={() => onChange(!value)}
                            disabled={disabled}
                          />
                          <label className="form-check-label" htmlFor={name}>
                            {t("screens:screen.settings.wifi.hiddenSsid")}
                          </label>
                        </div>
                      );
                    }}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="wifi.password">
                    {t("screens:screen.settings.wifi.password")}
                  </label>
                  <Input
                    id="wifi.password"
                    name="wifi.password"
                    type="password"
                    className="form-control"
                    disabled={disabled}
                  />
                </div>
              </div>
            )}
            {isVisible("accessControl") && (
              <>
                <AccessControlSettings
                  provider="shelly"
                  disabled={disabled || tuyaEnabled}
                  controllersData={{
                    controllers: shellyControllersData?.shellyControllers || [],
                  }}
                />
                <AccessControlSettings
                  provider="tuya"
                  disabled={disabled || shellyEnabled}
                  controllersData={{
                    controllers: tuyaControllersData?.tuyaControllers || [],
                  }}
                />
              </>
            )}
          </div>
          <div
            className={clsx(
              "col-lg-3",
              !hasVisible([
                "support",
                "scanner",
                "nfc",
                "dispenser",
                "photoCapture",
              ]) && "d-none",
            )}
          >
            {isVisible("support") && (
              <div className="form-group mb-4">
                <h6>{t("screens:screen.settings.support.title")}</h6>
                <div className="form-group">
                  <label htmlFor="supportEmail">
                    {t("screens:screen.settings.support.email")}
                  </label>
                  <Input
                    name="support.email"
                    autoComplete="email"
                    className="form-control"
                    disabled={disabled}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">
                    {t("screens:screen.settings.support.phone")}
                  </label>
                  <Input
                    name="support.phone"
                    className="form-control"
                    rules={{
                      validate: {
                        invalid: (value) => {
                          if (!value) {
                            return true;
                          }
                          return (
                            isValidPhoneNumber(value) ||
                            t("employee:input.error.phone.format")
                          );
                        },
                      },
                    }}
                    disabled={disabled}
                  />
                  <small className="form-text text-muted small">
                    {t("employee:input.label.phone-help")}
                  </small>
                </div>
              </div>
            )}
            {isVisible("scanner") && (
              <div className="form-group mb-4">
                <h6>{t("screens:screen.settings.scanner.title")}</h6>
                <small className="form-text text-muted small mb-2">
                  {t("screens:screen.settings.scanner.description")}
                </small>
                <Controller
                  name="scanner.enabled"
                  render={({
                    field: { value = false, name, onChange, ...rest },
                  }) => (
                    <div className="form-check my-2">
                      <input
                        id={name}
                        {...rest}
                        type="checkbox"
                        className="form-check-input"
                        disabled={disabled}
                        checked={value === true}
                        onChange={() => onChange(!value)}
                      />
                      <label className="form-check-label" htmlFor={name}>
                        {t("screens:screen.settings.scanner.enabled")}
                      </label>
                    </div>
                  )}
                />
                {scannerEnabled ? (
                  <>
                    <Controller
                      name="scanner.autoScan.enabled"
                      render={({
                        field: { value = false, name, onChange, ...rest },
                      }) => (
                        <div className="form-check ml-3 my-2">
                          <input
                            id={name}
                            {...rest}
                            type="checkbox"
                            className="form-check-input"
                            disabled={disabled}
                            checked={value === true}
                            onChange={() => onChange(!value)}
                          />
                          <label className="form-check-label" htmlFor={name}>
                            {t(
                              "screens:screen.settings.scanner.autoScan.enabled",
                            )}
                          </label>
                        </div>
                      )}
                    />
                    {scannerAutoScanEnabled ? (
                      <Controller
                        name="scanner.autoScan.type"
                        render={({ field: { value, onChange } }) => {
                          const options = [
                            {
                              value: "CHECKIN_AND_CHECKOUT",
                              label: t(
                                "screens:screen.settings.scanner.autoScan.type.checkinAndCheckout",
                              ),
                            },
                            {
                              value: "CHECKIN",
                              label: t(
                                "screens:screen.settings.scanner.autoScan.type.checkin",
                              ),
                            },
                            {
                              value: "CHECKOUT",
                              label: t(
                                "screens:screen.settings.scanner.autoScan.type.checkout",
                              ),
                            },
                          ];

                          return (
                            <div className="form-group ml-5 mt-2">
                              <label htmlFor="scanner-autoScan-type">
                                {t(
                                  "screens:screen.settings.scanner.autoScan.type.label",
                                )}
                              </label>
                              <Select
                                inputId="scanner-autoScan-type"
                                options={options}
                                value={options.find((o) => o.value === value)}
                                onChange={(selected) =>
                                  onChange(
                                    selected?.value ?? "CHECKIN_AND_CHECKOUT",
                                  )
                                }
                                isDisabled={disabled}
                              />
                            </div>
                          );
                        }}
                      />
                    ) : null}
                    {scannerAutoScanEnabled ? (
                      <Controller
                        name="scanner.autoScan.onlyQrScanner"
                        render={({
                          field: { value = false, name, onChange, ...rest },
                        }) => (
                          <div className="form-check ml-5 my-2">
                            <input
                              id={name}
                              {...rest}
                              type="checkbox"
                              className="form-check-input"
                              disabled={disabled}
                              checked={value === true}
                              onChange={() => onChange(!value)}
                            />
                            <label className="form-check-label" htmlFor={name}>
                              {t(
                                "screens:screen.settings.scanner.autoScan.onlyQrScanner.label",
                              )}
                            </label>
                            <small className="form-text text-muted small">
                              {t(
                                "screens:screen.settings.scanner.autoScan.onlyQrScanner.help",
                              )}
                            </small>
                          </div>
                        )}
                      />
                    ) : null}
                  </>
                ) : null}
              </div>
            )}
            {isVisible("nfc") && isClientNfcEnabled && (
              <div className="form-group mb-4">
                <h6>{t("screens:screen.settings.nfc.title")}</h6>
                <Controller
                  name="nfc.enabled"
                  render={({
                    field: { value = false, name, onChange, ...rest },
                  }) => (
                    <div className="form-check my-2">
                      <input
                        id={name}
                        {...rest}
                        type="checkbox"
                        className="form-check-input"
                        disabled={disabled}
                        checked={value === true}
                        onChange={() => onChange(!value)}
                      />
                      <label className="form-check-label" htmlFor={name}>
                        {t("screens:screen.settings.nfc.enabled")}
                      </label>
                    </div>
                  )}
                />
                {nfcEnabled ? (
                  <div className="form-group mb-2">
                    <label htmlFor="nfcSocketUrl">
                      {t("screens:screen.settings.nfc.socketUrl")}
                    </label>
                    <Input
                      name="nfc.socketUrl"
                      className="form-control"
                      disabled={disabled}
                    />
                  </div>
                ) : null}
              </div>
            )}
            {isVisible("dispenser") && (
              <div className="form-group mb-4">
                <h6>{t("screens:screen.settings.dispenser.title")}</h6>
                <div className="form-group mb-2">
                  <label htmlFor="dispenserId">
                    {t("screens:screen.settings.dispenser.id")}
                  </label>
                  <Input
                    name="dispenser.id"
                    className="form-control"
                    disabled={disabled}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="dispenserSocketUrl">
                    {t("screens:screen.settings.dispenser.socketUrl")}
                  </label>
                  <Input
                    name="dispenser.socketUrl"
                    className="form-control"
                    disabled={disabled}
                  />
                </div>
              </div>
            )}
            {isVisible("photoCapture") && isClientPhotoCaptureEnabled && (
              <div className="form-group mb-4">
                <h6>{t("screens:screen.settings.photoCapture.title")}</h6>
                <Controller
                  name="photoCapture.enabled"
                  render={({
                    field: { value = false, name, onChange, ...rest },
                  }) => (
                    <div className="form-check mt-2 mb-1">
                      <input
                        id={name}
                        {...rest}
                        type="checkbox"
                        className="form-check-input"
                        disabled={disabled}
                        checked={value === true}
                        onChange={() => onChange(!value)}
                      />
                      <label className="form-check-label" htmlFor={name}>
                        {t("screens:screen.settings.photoCapture.enabled")}
                      </label>
                    </div>
                  )}
                />
                {photoCaptureEnabled ? (
                  <div className="ml-4">
                    <MemberPhotoCaptureSettings
                      type="employee"
                      disabled={disabled}
                    />
                    <MemberPhotoCaptureSettings
                      type="visitor"
                      disabled={disabled}
                    />
                    <MemberPhotoCaptureSettings
                      type="meetingAttendee"
                      disabled={disabled}
                    />
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default GeneralSettings;
